import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { shallowEqualObjects } from 'shallow-equal';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

import { getPressTitle } from '../contracts/machines';
import {
  getPathName,
} from '../contracts/pressHMI.schema';
import usePLCStore from '../hooks/usePLCStore';

const PLCContext = createContext(null);

/**
 * Core ADS connection layer.  Accepts a pre-built `handles` array and wires
 * it to the ADS store.  Does NOT own group-switching logic — callers are
 * responsible for computing the handle list (see HMIHandleProvider for
 * the PressHMI dynamic-group wrapper).
 */
export default function PLCProvider({
  pressId,
  handles,
  plcUrl,
  debugPaths = null,
  children,
  test = false,
}) {
  const [writeError, setWriteError] = useState(null);
  const [activeWrite, setActiveWrite] = useState(null);

  const { get, subscribe, write } = usePLCStore({ plcUrl, test, handles });

  // Resolves true on success and false on failure (the message lands in
  // writeError), so fire-and-forget callers need no handling while callers
  // that sequence dependent writes can stop at the first failure.
  const runWrite = useCallback(async ({ path, value }) => {
    const handle = getPathName(path);

    setActiveWrite(handle);
    setWriteError(null);

    try {
      await write({ handle, value });
      return true;
    } catch (error) {
      setWriteError(error.message);
      return false;
    } finally {
      setActiveWrite(null);
    }
  }, [write]);

  useEffect(() => {
    if (test) {
      window.pressHMI = {
        get,
        subscribe,
        write: runWrite,
      };
      if (debugPaths) window.PLC_PATHS = debugPaths;

      return () => {
        if (window.pressHMI?.get === get) {
          delete window.pressHMI;
          if (debugPaths) delete window.PLC_PATHS;
        }
      };
    }
    return () => {};
  }, [get, runWrite, subscribe, test, debugPaths]);

  const value = useMemo(() => ({
    get,
    subscribe,
    write,
    pressId,
    runWrite,
    activeWrite,
    writeError,
    setWriteError,
  }), [get, subscribe, write, pressId, runWrite, activeWrite, writeError]);

  return (
    <PLCContext.Provider value={value}>
      {children}
    </PLCContext.Provider>
  );
}

PLCProvider.propTypes = {
  /** Pre-computed flat handle array — build from a schema's groups map. */
  handles: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  })).isRequired,
  plcUrl: PropTypes.string.isRequired,
  pressId: PropTypes.string.isRequired,
  /** Exposed as window.PLC_PATHS in test/dev mode for console debugging. */
  debugPaths: PropTypes.shape({}),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  test: PropTypes.bool,
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

export const usePLC = () => {
  const ctx = useContext(PLCContext);
  if (!ctx) throw new Error('usePLC must be used within a PLCProvider');
  return ctx;
};

const usePLCSubscribe = (selector) => {
  const ctx = usePLC();
  return useSyncExternalStore(ctx.subscribe, () => selector(ctx.get()));
};

export const usePLCReady = () => usePLCSubscribe((s) => s.ready);
export const usePLCError = () => usePLCSubscribe((s) => s.error);

export const usePLCValue = (handle) => {
  const key = getPathName(handle);
  return usePLCSubscribe((s) => s.values?.[key]);
};

export const usePLCValues = (handles) => {
  const cache = useRef({});
  return usePLCSubscribe((s) => {
    const next = handles.reduce((acc, h) => ({
      ...acc,
      [getPathName(h)]: s.values?.[getPathName(h)],
    }), {});
    if (!shallowEqualObjects(cache.current, next)) {
      cache.current = next;
    }
    return cache.current;
  });
};

export const usePLCWriteState = () => {
  const {
    activeWrite,
    runWrite,
    setWriteError,
    writeError,
  } = usePLC();

  return {
    runWrite,
    activeWrite,
    writeError,
    setWriteError,
  };
};

// ── PressHMI-specific hooks ──────────────────────────────────────────────────

/** Requires pressId to be set on the provider (PressHMI only). */
export const usePressTitle = () => {
  const { pressId } = usePLC();
  return getPressTitle(pressId);
};
